/**
 * Help Content Constants
 * Centralized help content for all pages in the application
 */

import type { HelpContent } from '../types/helpContent.types';

export const GANGS_HELP: HelpContent = {
  title: 'Gang Management',
  description:
    'This page allows you to create and manage gangs (teams of workers) for your construction organization. Gangs work together on sites, and their work is tracked collectively for payment processing.',
  sections: {
    whatIsIt: `A gang is a team of construction workers that:
• Work together on the same sites and plots
• Have a hierarchical structure (leaders and members)
• Share collective work tracking and payment processing
• Can be booked as a unit to construction projects`,
    additionalInfo: {
      'Gang Leaders vs Members': `Gang Leaders have full administrative control:
  ✓ Book the gang to construction sites
  ✓ Approve timesheets and payment claims
  ✓ Add/remove gang members
  ✓ Adjust member rates and configurations

Regular Members:
  • Complete assigned work on sites
  • Submit their own timesheets
  • View their gang's assignments`,
    },
    commonTasks: [
      'Create a new gang for a specific trade or project',
      'Add members to an existing gang',
      'Promote a member to gang leader',
      'Configure default rates for gang members',
      'View gang work history and payments',
    ],
    scenarios: [
      {
        title: 'Creating a Groundworks Gang',
        description:
          "You've won a new residential development contract requiring a dedicated groundworks team.",
        steps: [
          'Click "Add Gang" and name it "Groundworks Alpha"',
          'Add John Smith as gang leader (experienced foreman)',
          'Add 4 members: 2 excavator operators, 2 laborers',
          'Set default hourly rates for each role',
          'Save - gang is now ready to be booked to plots',
        ],
      },
      {
        title: 'Managing Gang Leader Changes',
        description:
          'Your gang leader has been promoted to site manager and needs to hand over gang leadership.',
        steps: [
          'Open the gang details panel',
          'Click edit leaders',
          'Remove outgoing leader, add new leader',
          'New leader can now approve timesheets and manage bookings',
        ],
      },
      {
        title: 'Adjusting Member Rates After Pay Rise',
        description:
          'A long-term gang member has received a pay rise that should apply to all future work.',
        steps: [
          'Open gang details panel',
          'Click edit members',
          'Find the member and update their hourly rate',
          'Save - new rate applies to all future bookings',
        ],
      },
    ],
    relatedPages: [
      {
        title: 'Sites',
        description: 'Book gangs to specific plots',
      },
      {
        title: 'Pot Approvals',
        description: 'Review and approve gang payment pots',
      },
      {
        title: 'Labour Groups',
        description: 'Assign work types gangs can perform',
      },
    ],
  },
};

export const USERS_HELP: HelpContent = {
  title: 'User Management',
  description:
    'This page allows you to manage all users in your construction organization. Users can be site managers, workers, admin staff, or gang leaders - each with different permissions and capabilities.',
  sections: {
    additionalInfo: {
      'User Roles & Permissions': `Your organization likely has these role types:
• Admin: Full system access, can manage all data
• Site Manager: Manages specific sites, approves work
• Gang Leader: Manages gang, approves member timesheets
• Worker: Books to work, submits timesheets
• Finance: Views financial data, processes payments
• Viewer: Read-only access to assigned areas`,
      'What are Labour Groups?': `When you assign a user to labour groups (like "Joiner", "Plumber", "Electrician"), you control:
  • What labour items they can see on sites
  • What work they can book themselves to
  • What type of work appears in their mobile app

This ensures electricians only see electrical work, plumbers only see plumbing work, etc.`,
    },
    commonTasks: [
      'Invite new users to your organization',
      'Assign roles and permissions to users',
      'Assign users to labour groups (defines what work they can see)',
      'View user activity and work history',
      'Deactivate users who leave the organization',
      'To view offboarded users, open the filter menu and toggle "Show offboarded users"',
    ],
    scenarios: [
      {
        title: 'Inviting a New Site Worker',
        description:
          "You've hired a new plumber who needs access to see and book plumbing work across your sites.",
        steps: [
          'Click "Invite User"',
          'Enter their email address',
          'Assign role: "Worker"',
          'Assign labour group: "Plumber"',
          "Send invitation - they'll receive setup email",
        ],
        result:
          'User can now log in and see only plumbing labour items across all sites they have access to.',
      },
      {
        title: 'Promoting a Worker to Gang Leader',
        description:
          'An experienced worker is now leading a team and needs permissions to manage gang bookings and approvals.',
        steps: [
          'Find the user in the table',
          'Open their details panel',
          'Change role from "Worker" to "Gang Leader"',
          'Assign them as leader in the Gangs page',
          'Save changes',
        ],
        result:
          'User can now manage their gang, approve timesheets, and book the gang to sites.',
      },
      {
        title: 'Onboarding a New Site Manager',
        description:
          "You're opening a new construction site and need a site manager with full oversight of that location.",
        steps: [
          'Invite user with "Site Manager" role',
          'Grant them site access permissions',
          'They can now approve work, manage plots, and oversee all workers on their assigned site',
        ],
      },
    ],
    relatedPages: [
      {
        title: 'Gangs',
        description: 'Organize users into working teams',
      },
      {
        title: 'Labour Groups',
        description: 'Manage the work categories users can access',
      },
      {
        title: 'Sites',
        description: 'See user activity and bookings on specific sites',
      },
    ],
  },
};

export const LABOUR_GROUPS_HELP: HelpContent = {
  title: 'Labour Item Groups',
  description:
    'This page allows you to manage labour item groups - categories that organise construction work by trade or skill type. Labour groups control what work users can see and book on sites.',
  sections: {
    whatIsIt: `Labour item groups categorize construction work by trade:
• Plumber: All plumbing-related tasks
• Joiner: Carpentry and joinery work
• Electrician: Electrical installation work
• Groundworks: Excavation, foundations, drainage
• Bricklayer: Masonry and brickwork

Each group contains specific labour items (tasks) that belong to that trade.`,
    additionalInfo: {
      'How Labour Groups Control Access': `When you assign a user to labour groups:
  ✓ They ONLY see labour items in those groups
  ✓ They can ONLY book to work in their assigned groups
  ✓ This ensures trade-specific work visibility

Example:
  User assigned to "Plumber" group:
    ✓ Sees: Install bathroom suite, Fit radiators, Lay pipework
    ✗ Cannot see: Hang doors (Joiner), Wire circuits (Electrician)`,
      'How This Works on Sites': `1. Site manager imports a site with all labour items
2. Labour items are categorized into groups (Plumber, Joiner, etc.)
3. Workers browse the site in their mobile app
4. They ONLY see items from their assigned labour groups
5. They book to available work in their trade
6. Site manager sees all work across all groups`,
    },
    commonTasks: [
      'Create a new labour group for a specific trade',
      'Add labour items to appropriate groups',
      'View which users are assigned to each group',
      'Reorganise labour items between groups',
      'Archive unused labour groups',
    ],
    scenarios: [
      {
        title: 'Setting Up Trade-Based Access',
        description:
          "You're organising your construction business and want plumbers to only see plumbing work.",
        steps: [
          'Create labour group called "Plumber"',
          'Add all plumbing-related labour items to this group',
          'Go to Users page and assign plumbers to "Plumber" group',
          'When plumbers open a site, they only see plumbing tasks',
        ],
        result: 'Clean, focused work lists for each trade.',
      },
      {
        title: 'Creating a Specialist Group',
        description:
          "You've started taking on HVAC contracts and need a new category for heating engineers.",
        steps: [
          'Click "Add Group"',
          'Name it "HVAC Engineer"',
          'Add labour items: Install boiler, Fit radiators, Commission heating system, etc.',
          'Assign HVAC engineers to this group',
        ],
        result: 'HVAC engineers see only their specialized work.',
      },
      {
        title: 'Multi-Skilled Workers',
        description:
          'Some workers are multi-skilled (e.g., can do both joinery and general construction).',
        steps: [
          'Go to Users page',
          'Find the multi-skilled user',
          'Assign them to MULTIPLE groups: "Joiner" AND "General Builder"',
        ],
        result:
          'User sees labour items from both groups and can book to either type of work.',
      },
    ],
    relatedPages: [
      {
        title: 'Users',
        description: 'Assign labour groups to users',
      },
      {
        title: 'Sites',
        description: 'View how labour items are organised on actual sites',
      },
      {
        title: 'Gangs',
        description: 'Gangs can work across multiple labour groups',
      },
    ],
  },
};

export const SITES_HELP: HelpContent = {
  title: 'Site Management - Plots & Labour',
  description:
    'This page shows the detailed breakdown of a construction site into plots (houses, units, or zones) and the labour work scheduled for each plot. You can track work progress, manage allocations, and approve variations.',
  sections: {
    whatIsIt: `Understanding the Hierarchy:

Site
 └─ Plot (e.g., House 23, Unit 4A)
     └─ Labour Item (e.g., "Install bathroom suite")
         ├─ Allocations (workers/gangs assigned to the item)
         └─ Variations (approved changes to scope/cost)`,
    additionalInfo: {
      'Typical Workflow': `1. Win Contract: Your company wins a construction contract
2. Import Site: Site manager imports the site structure
   • Site details (name, location, W3W address)
   • Plots (individual houses or units)
   • Labour items (tasks to be completed per plot)
3. Workers Browse: Workers view available work based on their labour group permissions
4. Book & Complete: Workers book to tasks, complete work, submit timesheets
5. Track Progress: Monitor completion status and costs`,
      'What You Can Do Here': `• View all plots on a site
• Expand plots to see labour items (tasks)
• Monitor work progress with progress bars
• View financial data (budgeted vs actual costs)
• See worker allocations to each task
• Review and approve variations (scope changes)
• Add new plots or labour items`,
      'How Labour Groups Filter Work': `• Workers see ONLY labour items matching their assigned groups
• Plumbers see plumbing tasks, electricians see electrical tasks
• Site managers see ALL work across all groups
• This filters the view in workers' mobile apps`,
      'Financial Tracking': `Each labour item shows:
• Budgeted amount (original quote)
• Progress percentage (work completed)
• Allocations (who's working on it)
• Variations (approved changes)

Site managers can track costs in real-time and identify budget overruns early.`,
    },
    commonTasks: [
      'Find a specific plot or labour item',
      'Check progress on a specific house/unit',
      'View who is allocated to which tasks',
      'Approve variation requests',
      'Monitor budget vs actual spend',
    ],
    scenarios: [
      {
        title: 'Tracking Plot Completion',
        description:
          'You need to check progress on House 23 before the client site visit tomorrow.',
        steps: [
          'Search for "House 23" or scroll to find it',
          'Click to expand the plot',
          'Review labour items and their completion status',
          'Check progress bars to identify incomplete work',
        ],
        result:
          'You can see that groundworks (100%), first fix electrical (80%), and plumbing rough-in (60%) are the current status.',
      },
      {
        title: 'Reviewing Worker Allocations',
        description:
          'A plot is behind schedule and you need to see who is allocated to the delayed work.',
        steps: [
          'Expand the delayed plot',
          'Find the delayed labour item (e.g., "Hang doors")',
          'Expand the labour item to see allocations',
          'View which workers/gangs are assigned',
          'Contact them or reassign work as needed',
        ],
      },
      {
        title: 'Approving a Variation',
        description:
          'A client has requested an upgraded bathroom suite, and you need to approve the additional cost.',
        steps: [
          'Find "Install bathroom suite" labour item',
          'Expand to see variations',
          'See variation: "Upgrade to premium suite - £450 extra"',
          'Review details and approve/reject',
          'Approved variations update the plot budget',
        ],
      },
    ],
    relatedPages: [
      {
        title: 'Labour Groups',
        description: 'Controls what work types users can see',
      },
      {
        title: 'Gangs',
        description: 'Book gangs to plots for team-based work',
      },
      {
        title: 'Pot Approvals',
        description: 'Approve payments for completed work',
      },
    ],
  },
};

export const LABOUR_ITEMS_HELP: HelpContent = {
  title: 'Labour Item Definitions',
  description:
    'This page allows you to manage labour item definitions - reusable task templates that define standard construction work. These definitions are used across all your sites and can be assigned to labour groups for trade-specific organization.',
  sections: {
    whatIsIt: `Labour item definitions are reusable task templates that:
• Define standard construction tasks (e.g., "Install bathroom suite", "Hang internal door")
• Include pricing and rate information
• Can be assigned to labour groups (Plumber, Joiner, etc.)
• Are reused across multiple sites and projects
• Provide consistency in task naming and pricing`,
    additionalInfo: {
      'How They Work Across Sites': `1. Create labour item definitions once (this page)
2. Assign definitions to labour groups
3. When importing a site, select which definitions apply
4. Definitions become available as tasks workers can book to
5. Consistent pricing and task names across all projects`,
      'Labour Items vs Labour Groups': `**Labour Item Definitions**: The actual tasks/work to be done
  • Example: "Install bathroom suite", "Fit kitchen units"
  • Created once, reused everywhere
  • Have pricing and descriptions

**Labour Groups**: Categories/trades that organize items
  • Example: "Plumber" group, "Joiner" group
  • Control which users see which items
  • Items are assigned to groups`,
      'Pricing and Rates': `Labour item definitions can include:
• Standard hourly rates for the task
• Fixed price per item
• Material costs
• Estimated hours to complete

These prices become defaults when the item is used on sites, but can be adjusted per-site if needed.`,
    },
    commonTasks: [
      'Create new labour item definitions for standard tasks',
      'Set default pricing and rates for definitions',
      'Assign definitions to appropriate labour groups',
      'Update existing definitions when rates change',
      'Search and filter definitions by name or group',
    ],
    scenarios: [
      {
        title: 'Creating Standard Plumbing Definitions',
        description:
          'You want to create reusable plumbing tasks that can be used across all residential projects.',
        steps: [
          'Click "Add Item" to create a new definition',
          'Name it "Install bathroom suite"',
          'Set default rate: £450 per installation',
          'Add description: "Supply and fit standard bathroom suite including basin, toilet, bath"',
          'Assign to labour group: "Plumber"',
          'Save definition',
        ],
        result:
          'This definition is now available when importing new sites, and plumbers will see it in their work list when assigned to plots.',
      },
      {
        title: 'Updating Rates Across All Projects',
        description:
          'Material costs have increased and you need to update the rate for a specific definition.',
        steps: [
          'Search for the definition: "Install kitchen units"',
          'Open the definition to edit',
          'Update rate from £1,200 to £1,350',
          'Save changes',
        ],
        result:
          'The new rate applies to all NEW site imports. Existing sites retain their current rates unless manually updated.',
      },
      {
        title: 'Organizing Definitions by Trade',
        description:
          'You have 50+ definitions and want to ensure each is assigned to the correct labour group.',
        steps: [
          'Review each definition in the list',
          'Check which labour group each is assigned to',
          'Edit definitions to assign to correct groups (Plumber, Joiner, Electrician, etc.)',
          'Ensure electricians see only electrical work, plumbers see only plumbing',
        ],
        result:
          'Workers see only relevant tasks in their trade when viewing sites.',
      },
      {
        title: 'Creating Multi-Trade Definitions',
        description:
          'Some tasks require multiple trades and should be visible to different groups.',
        steps: [
          'Create definition: "Kitchen installation - full package"',
          'Assign to multiple groups: "Plumber", "Electrician", "Joiner"',
          'Set rate: £3,500 (covers all trades)',
        ],
        result:
          'All three trade groups see this item and can coordinate on the complete kitchen installation.',
      },
    ],
    relatedPages: [
      {
        title: 'Labour Groups',
        description: 'Organize labour items into trade-based categories',
      },
      {
        title: 'Sites',
        description:
          'See how labour items are used on actual construction sites',
      },
      {
        title: 'Users',
        description:
          'Assign users to labour groups to control what work they see',
      },
    ],
  },
};

export const POT_APPROVALS_HELP: HelpContent = {
  title: 'Pot Approvals',
  description:
    'This page shows all payment pots awaiting your approval. Pots are collections of completed work over a period (usually weekly) that are ready to be paid out to workers or gangs.',
  sections: {
    whatIsIt: `A pot represents:
• Work completed over a specific period (e.g., Week 45, 2024)
• Associated with a specific worker or gang
• All work across all sites they worked on that period
• Requires approval before payment can be processed

Example Pot:
  "Groundworks Gang Alpha - Week 45"
  • Includes all work completed by the gang in Week 45
  • Aggregates work across multiple sites/plots
  • Shows total payment amount (e.g., £12,450.00)
  • Status: Submitted (awaiting your approval)`,
    additionalInfo: {
      'Pot Statuses': `• Submitted: Worker/gang has submitted, awaiting approval
• Verifying: Finance team is checking the details
• Missed Verification: The verification deadline passed with work still unapproved
• Approved: Approved for payment processing
• Paid: Payment has been processed
• Rejected: Sent back for corrections`,
      'When a Pot Misses the Verification Deadline': `A pot reaches "Missed Verification" when the verify deadline passes while some work is still unapproved. The pot stays put — it won't roll over. To clear it:

• Approve the outstanding work: once nothing is left unapproved, the pot becomes Verified and submits automatically if auto-submit is scheduled, otherwise it's ready to submit.
• Cancel the outstanding work: the pot becomes Verified, or is archived if no work was approved.
• Move to the active period: relocates the pot to the current week so the work can be resolved there. The pot stays in Missed Verification until it is.`,
      'The Approval Workflow': `1. Workers complete tasks and submit timesheets
2. System creates pots grouping work by period and worker/gang
3. Pots appear here with "Submitted" status
4. Site manager reviews and approves/rejects
5. Finance team processes approved pots
6. Workers receive payment`,
      'What You Can Do Here': `• Review submitted pots awaiting approval
• See who worked what and for how much
• View detailed breakdowns of work included in each pot
• Approve pots to send to finance for payment
• Reject pots to request corrections
• Filter by status, owner type, or time period`,
      'Understanding Pot Ownership': `Pots can belong to:
• Individual Workers: Solo work completed by one person
• Gangs: All work completed by a gang as a team
• Subcontractors: External contractor work

Each type may have different approval workflows and payment terms in your organization.`,
      'Financial Controls': `• You can see net amounts before approving
• Pots aggregate all work to prevent payment errors
• Approval trail creates audit history
• Rejected pots require resubmission and re-approval`,
    },
    commonTasks: [
      'Review and approve weekly submitted pots',
      'Check pot details before approving large amounts',
      'Reject pots with incorrect timesheets',
      'Track which pots have been paid',
      'Search for specific worker or gang pots',
    ],
    scenarios: [
      {
        title: 'Weekly Pot Approval Routine',
        description:
          "It's Monday morning and you have 15 pots submitted from last week awaiting approval.",
        steps: [
          'Sort by "Submitted" status (highlighted rows)',
          'Review each pot one by one',
          'Click pot to see detailed breakdown of work',
          'Verify hours and rates look correct',
          'Approve pots that are accurate',
          'Reject any with discrepancies for correction',
        ],
        result:
          'Approved pots move to finance for payment processing. Workers get paid this week.',
      },
      {
        title: 'Investigating a Large Pot Amount',
        description:
          'You see a pot for £18,000 which seems unusually high and want to verify before approving.',
        steps: [
          'Click the pot to open details panel',
          'Review breakdown by site and plot',
          'Check hours worked vs hourly rates',
          'Verify any overtime or premium rates',
          'Cross-check with site reports if needed',
          'Approve if correct, or contact gang leader to discuss',
        ],
      },
      {
        title: 'Rejecting an Incorrect Pot',
        description:
          'A worker has submitted a pot with incorrect hours due to a timesheet error.',
        steps: [
          'Open the pot details',
          'Identify the incorrect entry',
          'Click "Reject" with a reason: "Incorrect hours on House 23 plumbing - please verify and resubmit"',
          'Worker receives notification to correct and resubmit',
          'Corrected pot returns to "Submitted" status',
        ],
      },
    ],
    relatedPages: [
      {
        title: 'Sites',
        description: 'See the actual work completed that is in the pots',
      },
      {
        title: 'Gangs',
        description: 'View gang details and member information',
      },
      {
        title: 'Users',
        description: 'See individual worker profiles and work history',
      },
    ],
  },
};

export const OFFBOARDING_HELP: HelpContent = {
  title: 'Offboarding',
  description:
    'Manage the process of removing a user from your organisation. Offboarding happens in two phases — restrict access first, then remove completely once active work is resolved.',
  sections: {
    whatIsIt: `Offboarding is how you wind down a user's access to your organisation when they're leaving — whether a subcontractor finishing a job or a worker moving on.

Rather than cutting access immediately (which would orphan any in-progress work), Billda uses a two-phase approach:

Phase 1 — Start Offboarding: The user's role is swapped to a restricted "Offboarding" role. They can still submit pots, resubmit rejected work, and view their invoices, but they can't book new work, approve anything, or manage teams.

Phase 2 — Complete Offboarding: Once all active work is resolved (pots closed, gang memberships removed), you can complete the offboarding. The user loses all access to the organisation, but will retain access to their invoices. Their historical records — pots, invoices, bookings — are preserved for your records.`,
    additionalInfo: {
      'What can an offboarding user still do?': `The Offboarding role gives restricted access:
  ✓ View assigned sites, plots, and labour items
  ✓ Submit pots for the current period
  ✓ Resubmit rejected allocations
  ✓ Cancel their own allocations
  ✓ View their invoices

  ✗ Book new work
  ✗ Approve or reject anyone's work
  ✗ Manage gangs or teams
  ✗ Access admin features`,
      'What are blockers?': `Before completing offboarding, certain conditions must be resolved:

Active Pots — The user has pots that haven't reached a final state (Approved, Paid, or Archived). These need to be submitted and processed, or archived.

Gang Membership — The user is still a member of one or more gangs. Remove them from gangs before completing.

Gang Leadership — The user leads a gang with active pots. Transfer leadership to another member first.

Use the refresh button to check blockers again after resolving them.`,
      'Cancelling vs re-inviting': `If offboarding was started by mistake, or the user's plans change, you can cancel offboarding. This restores them to their previous role with full access — no re-invite needed.

If offboarding has already been completed (user is Inactive), you'll need to re-invite them through the normal invite flow. They'll go through onboarding again and reconnect to their historical data.`,
    },
    commonTasks: [
      'Start offboarding a subcontractor who has finished their contract',
      'Check what blockers remain before completing offboarding',
      'Complete offboarding once all active work is resolved',
      'Cancel offboarding if the user is staying',
      'Immediately revoke access when starting offboarding for security reasons',
    ],
    scenarios: [
      {
        title: 'Winding down a subcontractor',
        description:
          "A joiner has finished their last site and won't be returning. You want to remove their access once current pots are processed.",
        steps: [
          'Open the user\'s detail page and click "Offboard"',
          'Review the explanation and click "Start Offboarding"',
          "The user is now restricted — they can submit remaining pots but can't book new work",
          "Wait for their current week's pots to be submitted and approved",
          'Return to the offboarding page — check the blocker list',
          'Once clear, click "Complete Offboarding"',
        ],
        result:
          'The user is now Inactive. All their historical records remain for your audit trail. If they return in future, you can re-invite them.',
      },
      {
        title: 'Offboarding started by mistake',
        description:
          'An admin accidentally started offboarding the wrong user. The user is now restricted and confused.',
        steps: [
          'Open the user\'s detail page and click "Manage Offboarding"',
          'Click "Cancel Offboarding"',
          'Confirm the action in the dialog',
        ],
        result:
          "The user's previous role is restored and they regain full access on their next login.",
      },
      {
        title: 'Immediate access revocation',
        description:
          'A worker needs to be removed from site access immediately due to a safety concern.',
        steps: [
          'Open the user\'s detail page and click "Offboard"',
          'Click "Start Offboarding"',
          'Tick the "Revoke access immediately" checkbox in the confirmation dialog',
          'Confirm — the user is logged out immediately and must sign in again with restricted permissions',
        ],
        result:
          'The user is immediately restricted. Proceed with resolving blockers and completing offboarding when ready.',
      },
    ],
    relatedPages: [
      {
        title: 'Users',
        description: 'View and manage all users in your organisation',
      },
      {
        title: 'Gangs',
        description:
          'Manage gang memberships — remove offboarding users from gangs to clear blockers',
      },
      {
        title: 'Finance',
        description:
          'Review active pots that may be blocking offboarding completion',
      },
    ],
  },
};
